import type { ReactNode, JSX} from "react";
import type React from "react";
import { cn } from "./cn";

export interface ModuleListItemProps {
  readonly selected?: boolean;
  readonly children: ReactNode;
  /** Module-specific context menu handler. If omitted, right-click is silently suppressed. */
  readonly onContextMenu?: (e: React.MouseEvent) => void;
}

/**
 * Wrapper for list items in module list panes.
 * Handles selection state and hover styling.
 * Click handling is managed by the parent module pane renderer.
 * Plugins compose this with their own content.
 *
 * Context menu pattern:
 *   const ctx = useContextMenu<TItem>();
 *   <ModuleListItem onContextMenu={(e) => ctx.open(e, item)} ...>
 *   Then render <ContextMenu items={getItems(ctx.state.data)} ...> in the list root.
 */
export function ModuleListItem({
  selected,
  children,
  onContextMenu,
}: ModuleListItemProps): JSX.Element {
  return (
    <div
      data-testid="module-list-item"
      className={cn(
        "group mx-2 flex items-center gap-3 rounded-lg px-4 py-3 transition-colors duration-150",
        selected
          ? "bg-surface-selected"
          : "hover:bg-surface-hover",
      )}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu?.(e);
      }}
    >
      {children}
    </div>
  );
}
